import { useSettings, trackEvent } from "@/lib/settings";

// Returns a Tailwind visibility class based on separate mobile/desktop toggles.
// Full class strings are declared explicitly so Tailwind's JIT scanner picks them up.
const visibilityClass = (mobile, desktop, baseDisplay = "flex") => {
  const m = mobile !== false; // undefined => true (default visible)
  const d = desktop !== false;
  if (!m && !d) return "hidden";
  if (baseDisplay === "inline-flex") {
    if (m && d) return "inline-flex";
    if (m && !d) return "inline-flex md:hidden";
    return "hidden md:inline-flex"; // desktop only
  }
  // default "flex"
  if (m && d) return "flex";
  if (m && !d) return "flex md:hidden";
  return "hidden md:flex"; // desktop only
};

export default function StickyCTA({ onEnquire }) {
  const { settings } = useSettings();
  const whatsapp = settings?.contact?.whatsapp_number || "";
  const waLink = whatsapp ? `https://wa.me/${whatsapp.replace(/\D/g, "")}` : "#";
  const phone = settings?.contact?.phone || "";
  const cfg = settings?.sticky_cta || {};

  const enquireCls = visibilityClass(cfg.enquire_mobile, cfg.enquire_desktop, "inline-flex");
  const callCls = visibilityClass(cfg.phone_mobile, cfg.phone_desktop, "flex");
  const waCls = visibilityClass(cfg.whatsapp_mobile, cfg.whatsapp_desktop, "flex");

  const containerHidden = enquireCls === "hidden" && callCls === "hidden" && waCls === "hidden";
  if (containerHidden) return null;

  return (
    <div className="fixed bottom-4 md:bottom-6 right-4 md:right-6 z-40 flex flex-col items-end gap-3">
      <button
        onClick={() => { trackEvent("enquiry_cta_click", { location: "sticky" }); onEnquire(); }}
        className={`${enquireCls} bg-ink text-ivory px-5 py-3.5 text-[11px] uppercase tracking-[0.2em] font-medium shadow-[0_18px_40px_-12px_rgba(0,0,0,0.45)] hover:bg-clay transition-colors`}
        data-testid="sticky-enquire-btn"
      >
        Enquire now
      </button>
      <a
        href={phone ? `tel:${phone.replace(/\s+/g, "")}` : "#"}
        onClick={() => trackEvent("call_click", { location: "sticky" })}
        className={`${phone ? callCls : "hidden"} items-center justify-center w-14 h-14 rounded-full bg-clay text-ivory shadow-[0_18px_40px_-12px_rgba(158,123,79,0.7)] hover:scale-[1.05] transition-transform`}
        data-testid="sticky-call"
        aria-label="Call us"
      >
        <svg viewBox="0 0 24 24" className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
        </svg>
      </a>
      <a
        href={waLink}
        target="_blank"
        rel="noreferrer"
        onClick={() => trackEvent("whatsapp_click", { location: "sticky" })}
        className={`${waCls} items-center justify-center w-14 h-14 rounded-full bg-[#25D366] text-white shadow-[0_18px_40px_-12px_rgba(37,211,102,0.6)] hover:scale-[1.05] transition-transform`}
        data-testid="sticky-whatsapp"
        aria-label="WhatsApp"
      >
        <svg viewBox="0 0 32 32" className="w-7 h-7" fill="currentColor">
          <path d="M19.11 17.32c-.27-.13-1.6-.79-1.84-.88-.25-.09-.43-.13-.61.13-.18.27-.7.88-.86 1.06-.16.18-.32.2-.59.07-.27-.13-1.14-.42-2.16-1.33-.8-.71-1.34-1.6-1.5-1.87-.16-.27-.02-.41.12-.54.12-.12.27-.32.4-.48.13-.16.18-.27.27-.45.09-.18.04-.34-.02-.48-.07-.13-.61-1.47-.83-2.01-.22-.53-.45-.46-.61-.47l-.52-.01c-.18 0-.48.07-.73.34-.25.27-.96.94-.96 2.29 0 1.35.99 2.66 1.13 2.84.13.18 1.94 2.97 4.71 4.16.66.28 1.18.45 1.58.58.66.21 1.27.18 1.74.11.53-.08 1.6-.65 1.83-1.28.23-.63.23-1.17.16-1.28-.07-.11-.25-.18-.52-.31zM16 4C9.37 4 4 9.37 4 16c0 2.07.52 4.02 1.44 5.73L4 28l6.43-1.43A11.94 11.94 0 0 0 16 28c6.63 0 12-5.37 12-12S22.63 4 16 4zm0 21.5c-1.8 0-3.55-.48-5.08-1.4l-.36-.21-3.81.85.85-3.71-.24-.38A9.49 9.49 0 0 1 6.5 16c0-5.24 4.26-9.5 9.5-9.5s9.5 4.26 9.5 9.5-4.26 9.5-9.5 9.5z" />
        </svg>
      </a>
    </div>
  );
}
